//#region [IMPORT]

// === THIRD-PARTY ===
import {EventEmitter} from 'events';

// === CONFIG ===
import { protocols } from '../../config.js';

//#endregion

//#region [VALUES]

let _websocket;
const _events = new EventEmitter();
_events.setMaxListeners(2000)

const _pdwebserver_uri = 
  process.env.REACT_APP_PD_WEB_SERVER
? process.env.REACT_APP_PD_WEB_SERVER
: window.location.origin + '/pdwebserver/';

const _wss_uri = _pdwebserver_uri.replace(protocols.HTTP, protocols.WS);

// === RECONNECTION ===
const _reconnect_limit = 5 * 60 * 1000; // 5 minutes
const _reconnect_interval_initial =  250;

let _reconnect_timeout = null;
let _reconnect_attempt = 0;
let _websocket_token = null;

let focused_image = null;
let focused_field = null;
let focused_block = null;
let focused_documents = [];

let _reconnect_start_time = null;
let _last_open_time = 0;
let _pending_sends = [];
let _connecting = false;

// === MESSAGES ===
const _message_types = [
  'USERS',
  'FILE',
  'FILE-INFO',
  'FILE-RULES',
  'TASK',
  'TASK-CLEAR',
  'TASK-QUEUE-INFO',
  'TASK-QUEUE-INFO-UPDATE',
  'TASK-QUEUE-INFO-REMOVE',
  'DOCTYPES',
  'DOCUMENTS',
  'FIELD-UPDATE',
  'FIELD-CREATE',
  'FIELD-REMOVE',
  'WORKFLOW-MONITOR',
  'WORKFLOW-MONITOR-UPDATE',
  'ON-DEMAND-BATCHES',
  'ON-DEMAND-BATCHES-UPDATE',
  'ON-DEMAND-BATCHES-REMOVE',
];

//#endregion

export const socket = {
  onConnection,
  onDisconnection,
  onReconnection,
  onReconnectFail,
  onInactive,
  connect,
}

//#region [red] === TASK

//-----------------------------------------------------------------
function resetTask() {
  //-----------------------------------------------------------------
  clearFocus();

  _events.emit( 'TASK', {} );
  _events.emit( 'TASK-UPDATE', { status: { accepted: 0, total: 0, is_complete: false } } );
}

//-----------------------------------------------------------------
export function clearFocus() {
  //-----------------------------------------------------------------
  focused_image = null;
  focused_field = null;
  focused_block = null;
  focused_documents = [];
}

//#endregion

//#region [green] === Reconnection

window.addEventListener('online', () => {
  console.log('Network restored, reconnecting...');
  if (!isOpen() && !isConnecting()) {
    connect();
  }
});

window.addEventListener('offline', () => {
  console.warn('Network lost');
});

//-----------------------------------------------------------------
const calc_reconnect_interval = () => {
  const base = _reconnect_interval_initial * Math.pow(2, _reconnect_attempt);

  // Cap at 10s
  const capped = Math.min(base, 10000);

  // Ensure a minimum delay so we don't spam reconnects
  const min = 500;

  // Add jitter but keep within [min, capped]
  return Math.max(min, Math.random() * capped);
};

//-----------------------------------------------------------------
function reconnect() {
  //-----------------------------------------------------------------
  if (_reconnect_timeout) {
    clearTimeout(_reconnect_timeout);
    _reconnect_timeout = null;
  }

  if (!_reconnect_start_time) {
    _reconnect_start_time = Date.now();
  }

  if (Date.now() - _reconnect_start_time > _reconnect_limit) {
    _events.emit('reconnect-fail');
    _reconnect_start_time = null;
    _reconnect_attempt = 0;
    return;
  }

  if (_reconnect_attempt === 0) {
    console.log('Starting reconnect loop...');
    _events.emit('reconnection');
  }
  
  const reconnect_interval = calc_reconnect_interval();

  _reconnect_timeout = setTimeout(() => {
    _reconnect_timeout = null;

    // Guard against rapid-fire reconnect storms
    if (isOpen() || isConnecting()) {
      return;
    }

    _reconnect_attempt += 1;
    console.log(`Reconnect attempt #${_reconnect_attempt} in ${reconnect_interval.toFixed(0)}ms`);

    connect();
  }, reconnect_interval);
}
//#endregion

//-----------------------------------------------------------------
export function getSocketId() {
  //-----------------------------------------------------------------
  return _websocket_token;
}

//-----------------------------------------------------------------
function connect() {
  //-----------------------------------------------------------------
  if (_connecting) return;
  _connecting = true;
  
  if (
    _websocket?.readyState === WebSocket.OPEN ||
    _websocket?.readyState === WebSocket.CONNECTING
  ) {
    _connecting = false;
    return;
  }

  try {
    if (_websocket) {
      _websocket.removeEventListener('open', handleOpen);
      _websocket.removeEventListener('close', handleClose);
      _websocket.removeEventListener('error', handleError);
      _websocket.removeEventListener('message', handleMessage);

      try {
        _websocket.close();
      } catch (e) {
        // ignore close failures on stale sockets
      }
    }

    _websocket = new WebSocket(_wss_uri);

    _websocket.addEventListener('open', handleOpen);
    _websocket.addEventListener('close', handleClose);
    _websocket.addEventListener('error', handleError);
    _websocket.addEventListener('message', handleMessage);
  } catch (error) {
    console.warn(error);
  }
}

//-----------------------------------------------------------------
function handleOpen(event) {
  //-----------------------------------------------------------------
  try {
    console.log(`WebSocket connection opened.`);

    _connecting = false;
    _last_open_time = Date.now();
    _reconnect_start_time = null;

    const data = {};
    if (_websocket_token) {
      data.token = _websocket_token;
    }

    _events.emit('connection', data );

    // Attempt session reattach if we already have a token
    if (_websocket_token) {
      try {
        const message = {
          type: 'SESSION-REATTACH',
          data: {
            session_id: _websocket_token
          }
        };
        _websocket.send(JSON.stringify(message));
      } catch (e) {
        console.warn('Failed to send SESSION-REATTACH', e);
      }
    }

    // Flush pending sends immediately on open (do NOT wait for heartbeat)
    while (_pending_sends.length) {
      const fn = _pending_sends.shift();
      try { fn(); } catch (e) { console.warn('send failed', e); }
    }

    _reconnect_attempt = 0;
  } catch (error) {
    console.warn(error);
  }
}


//-----------------------------------------------------------------
function handleMessage(event) {
  //-----------------------------------------------------------------
  try {
    const message = JSON.parse(event.data);

    const is_valid_message = _message_types.includes(message.type);

    if(!is_valid_message) {
      // console.warn(`Message of type [${message.type}] is unsupported.`);
    }

    // Handle explicit session reattach signal from backend
    if (message.type === 'SESSION-REATTACHED') {
      try {
        console.log('Session successfully reattached');

        // Force refresh of critical state
        _events.emit('SESSION-REATTACHED', message.data);

        // Optionally request fresh task state as a safety net
        send(() => {
          const msg = { type: 'TASK-STATUS-REQUEST', data: {} };
          _websocket.send(JSON.stringify(msg));
        });

      } catch (e) {
        console.warn('Error handling SESSION-REATTACHED', e);
      }
      return;
    }

    if( message.type === 'IMAGE-FOCUS' ) {
      setFocusedImage( message.data );
    } else if(message.type === 'FIELD-FOCUS') {
      setFocusedField( message.type, message.data );
    } else if(message.type === 'BLOCK-FOCUS') {
      _events.emit( message.type, message.data );
      focused_block = message.data;
    } else if(message.type === 'DOCUMENT-FOCUS') {
      focused_documents = message.data.filter(doc => doc.open);
      message.data.forEach((doc) => {
        _events.emit('DOCUMENT-FOCUS-' + doc.id, { open: doc.open });
      });
    } else if( message.type === 'token' ) {
      _websocket_token = message.data.token;
    } else {
      _events.emit( message.type, message.data );
    }

  } catch (error) {
    console.warn(error);
  }
}

//-----------------------------------------------------------------
function handleError(event) {
  //-----------------------------------------------------------------
  _connecting = false;
  console.warn('Socket error:');
  console.error(event);

  if (!isOpen()) {
    reconnect();
  }
}

//-----------------------------------------------------------------
function handleClose(event) {
  //-----------------------------------------------------------------
  try {
    _connecting = false;
    
    const message_close = event.wasClean ? `cleanly` : 'unexpectedly';
    console.log(`Websocket closed ${message_close} (${event.code}).`);

    const lived = Date.now() - _last_open_time;

    if (lived < 5000) {
      // unstable connection → keep backoff growing
    } else {
      // stable connection → reset backoff
      _reconnect_attempt = 0;
    }

    _events.emit('disconnection', {code: event.code} );

    if (event.code === 1006 && _reconnect_attempt > 5) {
      console.warn('Too many rapid failures, cooling down');
      setTimeout(connect, 10000);
      return;
    }

    if( event.code === 1000 ) {
      // Normal Closure
      _events.emit('reconnect-fail');
      _reconnect_start_time = null;
      _reconnect_attempt = 0;
    }
    else if( event.code === 1001 ) {
      // Going Away - page refresh
    }
    else if( event.code === 1005 ) {
      // No Status Received
      reconnect();
    }
    else if( event.code === 1006 ) {
      // Abnormal Closure
      // code 1006 is reserved for 'abnormal closure' such as network lost or
      // failed connection due to invalid authorization
      console.warn('Abnormal close, scheduling reconnect...');
      reconnect();
    } 
    else if( event.code === 3000 ) {
      // Inactive
      _websocket_token = null;
      _events.emit('inactive');
    }
    else if( event.code === 3500 ) {
      // New connection established
    } 
    else if( event.code === 3999 ) {
      // server restart → allow reconnect instead of forcing inactive
      reconnect();
    } else {
      reconnect();
    }
  } catch (error) {
    console.warn('Invalid WS message:', event.data, error);
  }
}

//-----------------------------------------------------------------
export function emit( type, data ) {
  //-----------------------------------------------------------------
  _events.emit( type, data );
}

//-----------------------------------------------------------------
export function sendMessage( message ) {
  //-----------------------------------------------------------------
  send( () => _websocket.send( message ) );
}

//-----------------------------------------------------------------
export function sendMessageData( type, data ) {
  //-----------------------------------------------------------------
  const message = JSON.stringify( { type, data } );
  send( () => _websocket.send( message ) );
}

//-----------------------------------------------------------------
export function setFocusedImage( data ) {
  //-----------------------------------------------------------------
  if(focused_image) _events.emit( 'IMAGE-FOCUS-' + focused_image.id, { clear: true } );
  if( !data ) return;
  _events.emit( 'IMAGE-FOCUS-' + data.id, {} );
  _events.emit( 'IMAGE', data );
  focused_image = data;
}

//-----------------------------------------------------------------
export function setFocusedField( type, data ) {
  //-----------------------------------------------------------------
  if (!data) return;
  _events.emit( 'FIELD-FOCUS-' + data.id, data );
  _events.emit( type, data );
  focused_field = data;
}

//#region [purple] === LISTENERS

//-----------------------------------------------------------------
export function on(name, callback = ()=>{}) {
  //-----------------------------------------------------------------
  _events.on( name, callback );
}

//-----------------------------------------------------------------
function onConnection(cb) {
  //-----------------------------------------------------------------
  if (_websocket?.readyState === WebSocket.OPEN) {
    console.log('Connection already open');
    cb({ token: _websocket_token });
  } else {
    _events.once('connection', cb);
  }
}

//-----------------------------------------------------------------
export function onReconnection(cb) {
  //-----------------------------------------------------------------
  _events.on('reconnection', cb );
}

//-----------------------------------------------------------------
export function onDisconnection(cb) {
  //-----------------------------------------------------------------
  _events.on('disconnection', cb );
}

//-----------------------------------------------------------------
export function onReconnectFail(cb) {
  //-----------------------------------------------------------------
  _events.on('reconnect-fail', cb );
}

//-----------------------------------------------------------------
export function onInactive(cb) {
  //-----------------------------------------------------------------
  _events.on('inactive', cb );
}

// === REMOVE ===

//-----------------------------------------------------------------
export function removeAllListeners(names = []) {
  names.forEach(name => _events.removeAllListeners(name));
}

//-----------------------------------------------------------------
export function removeListener(name, listener) {
  //-----------------------------------------------------------------
  _events.removeListener(name, listener);
}

//-----------------------------------------------------------------
function removeTaskListener( listener ) {
  //-----------------------------------------------------------------
  _events.removeListener('TASK', listener);
}

//-----------------------------------------------------------------
function removeClearTaskListener( listener ) {
  //-----------------------------------------------------------------
  _events.removeListener('TASK-CLEAR', listener);
}

//-----------------------------------------------------------------
function removeDisconnectListener( listener ) {
  //-----------------------------------------------------------------
  _events.removeListener('disconnection', listener);
}

//-----------------------------------------------------------------
function removeReconnectListener( listener ) {
  //-----------------------------------------------------------------
  _events.removeListener('reconnection', listener);
}

//-----------------------------------------------------------------
function removeReconnectFailListener( listener ) {
  //-----------------------------------------------------------------
  _events.removeListener('reconnect-fail', listener);
}

//-----------------------------------------------------------------
function removeDoctypesListener( listener ) {
  //-----------------------------------------------------------------
  _events.removeListener('DOCTYPES', listener);
}

//-----------------------------------------------------------------
function removeDocumentsListener( listener ) {
  //-----------------------------------------------------------------
  _events.removeListener('DOCUMENTS', listener);
}

//-----------------------------------------------------------------
function removeAddFieldsListener( listener ) {
  //-----------------------------------------------------------------
  _events.removeListener('FIELD-CREATE', listener);
}

//-----------------------------------------------------------------
function removeRemoveFieldsListener( listener ) {
  //-----------------------------------------------------------------
  _events.removeListener('FIELD-REMOVE', listener);
}

//-----------------------------------------------------------------
function removeTaskQueueListener( listener ) {
  //-----------------------------------------------------------------
  _events.removeListener('TASK-QUEUE-INFO', listener);
}

//-----------------------------------------------------------------
function removeUpdateTaskQueueListener( listener ) {
  //-----------------------------------------------------------------
  _events.removeListener('TASK-QUEUE-INFO-UPDATE', listener);
}

//-----------------------------------------------------------------
function removeRemoveTaskQueueItemListener( listener ) {
  //-----------------------------------------------------------------
  _events.removeListener('TASK-QUEUE-INFO-REMOVE', listener);
}

//#endregion

//#region [blue] === SEND

//-----------------------------------------------------------------
function send(func = ()=>{} ) {
  if (isOpen()) {
    func();
    return;
  }

  if (!isConnecting()) {
    connect();
  }

  if (_pending_sends.length > 250) {
    _pending_sends.shift();
  }

  _pending_sends.push(func);
}

// === SETTINGS ===

//-----------------------------------------------------------------
export function requestCorrectionMode() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'CORRECTION-MODE-REQUEST' };
    _websocket.send( JSON.stringify( message ) );
  });
}

//-----------------------------------------------------------------
export function toggleCorrectionMode() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'CORRECTION-MODE-TOGGLE' };
    _websocket.send( JSON.stringify( message ) );
  });
}

//-----------------------------------------------------------------
export function setCorrectionModeAll() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'CORRECTION_MODE_ALL' };
    _websocket.send( JSON.stringify( message ) );
  });
}

export function setCorrectionModeExceptions() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'CORRECTION_MODE_EXCEPTIONS' };
    _websocket.send( JSON.stringify( message ) );
  });
}

export function setCorrectionModeHybrid() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'CORRECTION_MODE_HYBRID' };
    _websocket.send( JSON.stringify( message ) );
  });
}

// === TASK QUEUE ===

//-----------------------------------------------------------------
export function subscribeTaskQueue() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'TASK-QUEUE-SUBSCRIBING' };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function unsubscribeTaskQueue() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'TASK-QUEUE-UNSUBSCRIBE' };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function subscribeTaskQueueInfo() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'TASK-INFOS-SUBSCRIBING' };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function unsubscribeTaskQueueInfo() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'TASK-INFOS-UNSUBSCRIBE' };
    _websocket.send(JSON.stringify(message));
  });
}

// === TASK ===

//-----------------------------------------------------------------
export function requestTask(id) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'TASK-DEMAND', data: { id } };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function retainTask() {
  //-----------------------------------------------------------------
  resetTask();
  
  send( () => {
    const message = { type: 'TASK-RETAIN', data: { } };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function returnTask(reason) {
  //-----------------------------------------------------------------
  resetTask();

  send( () => {
    const message = { type: 'TASK-CANCEL', data: { reason } };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function submitTask() {
  //-----------------------------------------------------------------
  resetTask();

  send( () => {
    const message = { type: 'TASK-SUBMIT', data: {} };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function requestTaskStatus() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'TASK-STATUS-REQUEST', data: {} };
    _websocket.send(JSON.stringify(message));
  });
}

// === DOCUMENT ===

//-----------------------------------------------------------------
export function splitDocumentAtPageNumber(page_id) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'DOCUMENT-SPLIT', data: {page_id} };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function mergeDocumentAtPageNumber(page_id) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'DOCUMENT-MERGE', data: {page_id} };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function requestDocument(id) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'DOCUMENT', data: {id} };
    _websocket.send(JSON.stringify(message));
  });
}

export function requestThumbNavigation(dir) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'THUMB-NAVIGATION-REQUEST', data: {dir}};
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function requestDocIdFromField(id) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'DOC-ID-REQUEST', data: {id} };
    _websocket.send(JSON.stringify(message));
  });
}

// === BLOCKS ===

//-----------------------------------------------------------------
export function requestBlock(id) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'BLOCK', data: {id} };
    _websocket.send(JSON.stringify(message));
  });
}

// === IMAGES ===

//-----------------------------------------------------------------
export function requestImage(id) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'IMAGE', data: {id} };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function requestAuditImage(id) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'AUDIT-IMAGE', data: {id} };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function resetImage( id, url ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'IMAGE-RESET', data: { id, url } };
    _websocket.send(JSON.stringify(message));
  });
}

// === FIELD ===

//-----------------------------------------------------------------
export function requestField(id) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'FIELD', data: {id} };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function updateField( source, id, name, entry ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'FIELD-UPDATE', data: { source, field: { id, name, entry } } };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function updateFieldCoords(field) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'FIELD-COORD-UPDATE', data: field };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function removeEntry(field_id, entry_index) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'FIELD-ENTRY-REMOVE', data: {field_id,entry_index} };
    _websocket.send(JSON.stringify(message));
  });
}

export function goToPageNumber(value) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'PAGE-NUMBER-SEARCH', data: {value} };
    _websocket.send(JSON.stringify(message));
  });
}

export function requestFirstFieldOfThePage(id) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'FIRST-FIELD-PAGE-REQUEST', data: { id } };
    _websocket.send(JSON.stringify(message));
  });
}

// === TABLE ===

//-----------------------------------------------------------------
export function addTableRow({field_id, isAfter}) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'TABLE-ROW-CREATE', data: {field_id, isAfter} };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function removeTableRow({field_id}) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'TABLE-ROW-REMOVE', data: {field_id} };
    _websocket.send(JSON.stringify(message));
  });
}

// === MONITOR ===

//-----------------------------------------------------------------
export function subscribeWorkflowMonitor() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'WORKFLOW-MONITOR-SUBSCRIBING' };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function unsubscribeWorkflowMonitor() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'WORKFLOW-MONITOR-UNSUBSCRIBE' };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function workflowMonitorSettings( settings={} ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'WORKFLOW-MONITOR-SETTINGS', data: {settings} };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function workflowMonitorSort(column_name) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'WORKFLOW-MONITOR-SORT', data: {column_name} };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function workflowMonitorPage(value) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'WORKFLOW-MONITOR-PAGE', data: {value} };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function workflowMonitorItemsPerPage(value) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'WORKFLOW-MONITOR-ITEMS-PER-PAGE', data: {value} };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function workflowMonitorSearch(value) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'WORKFLOW-MONITOR-SEARCH', data: {value} };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function workflowMonitorBatchDelete(batches) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'WORKFLOW-MONITOR-DELETE', data: {batches} };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function workflowMonitorBatchPriority(batches, priority) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'WORKFLOW-MONITOR-PRIORITY', data: {batches, priority} };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function workflowMonitorBatchDownload() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'WORKFLOW-MONITOR-BATCHES-DOWNLOAD' };
    _websocket.send( JSON.stringify( message ) );
  });
}

//-----------------------------------------------------------------
export function workflowMonitorBulkUpdate(updateData) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'WORKFLOW-MONITOR-BATCHES-BULK-UPDATE', data: updateData };
    _websocket.send( JSON.stringify( message ) );
  });
}

// === ON-DEMAND ===

//-----------------------------------------------------------------
export function subscribeOdBatchList() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'subscribeOdBatchList' };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function unsubscribeOdBatchList() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'unsubscribeOdBatchList' };
    _websocket.send(JSON.stringify(message));
  });
}

// === FILES ===

//-----------------------------------------------------------------
export function requestFile(id) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'FILE-REQUEST', data: { id } };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function requestFileInfo() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'FILE-INFO-REQUEST' };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function requestFileResults(id) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'FILE-EXPORT', data: { id } };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function requestFileEvaluation(id) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'FILE-EVALUATE', data: { id } };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function requestFileRuleDocuments(file_id) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'FILE-RULE-DOCUMENTS-REQUEST', data: { file_id } };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function requestDocumentRules( file_id, numcode ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'DOCUMENT-RULES-REQUEST', data: { file_id, numcode } };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function requestDocumentRuleTasks( file_id, numcode ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'DOCUMENT-TASKS-REQUEST', data: { file_id, numcode } };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function assignFile(user_id, file_id) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'FILE-ASSIGN', data: { user_id, file_id } };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function overruleFileException( id, comment ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'FILE-EXCEPTION-OVERRULE', data: { id, comment } };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function confirmFileException( id ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'FILE-EXCEPTION-CONFIRM', data: { id } };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function updateFileRuleComment( id, comment ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'FILE-RULE-COMMENT-UPDATE', data: { id, comment } };
    _websocket.send(JSON.stringify(message));
  });
}

// === AUDIT ===

//-----------------------------------------------------------------
export function requestAuditTask( file_id, exception_id ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'AUDIT-TASK-REQUEST', data: { file_id, exception_id } };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function updateAuditField( field_id, changes ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'AUDIT-FIELD-UPDATE', data: { field_id, changes } };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function subscribeAuditQueue() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'AUDIT-QUEUE-SUBSCRIBING' };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function unsubscribeAuditQueue() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'AUDIT-QUEUE-UNSUBSCRIBE' };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function subscribeAuditQueueInfo() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'AUDIT-INFOS-SUBSCRIBING' };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function unsubscribeAuditQueueInfo() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'AUDIT-INFOS-UNSUBSCRIBE' };
    _websocket.send(JSON.stringify(message));
  });
}

// === USERS ===

//-----------------------------------------------------------------
export function requestUsers( show_disabled = false ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'USERS-REQUEST', data: { show_disabled } };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function requestUserListDownload() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'USER-LIST-DOWNLOAD-REQUEST' };
    _websocket.send( JSON.stringify( message ) );
  });
}

// === STATS ===

//-----------------------------------------------------------------
export function requestStatsScehdule() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'STATS-SCHEDULE-REQUEST' };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function createStatsScheduleTask( name, date_start, time, frequency, recipients ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'STATS-SCHEDULE-CREATE', data: { name, date_start, time, frequency, recipients } };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function removeStatsScheduleTask( id ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'STATS-SCHEDULE-REMOVE', data: { id } };
    _websocket.send(JSON.stringify(message));
  });
}


// === SYSTEM MANAGEMENT === 

//-----------------------------------------------------------------
export function setTaskMonitoring(state) {
  //-----------------------------------------------------------------
  _events.emit('TASK-MONITOR-STATUS', state);
}

//-----------------------------------------------------------------
export function requestTaskMonitorStatus() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'TASK-MONITOR-STATUS-REQUEST' };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function stopTaskMonitor() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'TASK-MONITOR-STOP' };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function startTaskMonitor() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'TASK-MONITOR-START' };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function systemRestart() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'SYSTEM-RESTART' };
    _websocket.send(JSON.stringify(message));
  });
}

export function batchReset() { 
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'BATCH-RESET-REQUEST' };
    _websocket.send(JSON.stringify(message));
  });
}

export function requestServerStartTime() {
  //-----------------------------------------------------------------
  send(() => {
    const message = { type: 'START-TIME-REQUEST' };
    _websocket.send(JSON.stringify(message));
  });
}

// === DATA PROCESSING ===

//-----------------------------------------------------------------
export function requestTasksValidated( data ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'TASKS-VALIDATED-REQUEST', data: { data } };
    _websocket.send(JSON.stringify(message));
  });  
}

//-----------------------------------------------------------------
export function requestTasksAvailable(data) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'TASKS-AVAILABLE-REQUEST', data: { data }};
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function requestUsersAvailable(data) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'USERS-AVAILABLE-REQUEST', data:{data}  };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function requestUsersActive(data) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'USERS-ACTIVE-REQUEST', data:{data} };
    _websocket.send(JSON.stringify(message));
  });
}


// === RESOURCE UTILIZATION === 

//-----------------------------------------------------------------
export function requestCPUMonitor( cpu ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'CPU-MONITOR-REQUEST', data: { cpu } };
    _websocket.send(JSON.stringify(message));
  });
} 

//-----------------------------------------------------------------
export function requestMemoryMonitor( mem ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'MEMORY-MONITOR-REQUEST', data: { mem }  };      
    _websocket.send(JSON.stringify(message));
  });
}    

//-----------------------------------------------------------------
export function requestSystemMemTotal( total ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'SYSTEM-MEMORY-TOTAL-REQUEST', data: { total } };      
    _websocket.send(JSON.stringify(message));
  });
}  

// === USER DETAILS	===

//-----------------------------------------------------------------
export function requestDisabledUsers(id) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'DISABLED-USERS-REQUEST', data: { id } };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function createUserBulk( data ) {
 //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'USER-CREATE-BULK', data: { users: data }}
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function editUsersBulk( data ) {
 //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'USERS-EDIT-BULK', data: { users: data }}
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function createUser ( data ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'USER-CREATE', data: { user: data }} 
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function userEdit( id, data ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'USER-UPDATE-REQUEST', data: { id, data }};
    _websocket.send(JSON.stringify(message));
  });
}

// export function passwordReset(id) {
//   send( () => {
//     const message = { type: 'PASSWORD-RESET-REQUEST', data: { id }};
//     _websocket.send(JSON.stringify(message));
//   });
// }

//-----------------------------------------------------------------
export function userDisable( id ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'USER-DISABLE-REQUEST', data: { id }};
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function userEnable( id ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'USER-ENABLE-REQUEST', data: { id }};
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function userRoleCreate( user_id, role_id ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'USER-ROLE-CREATE-REQUEST', data: { user_id, role_id }};
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function userRoleRemove( user_id, role_id ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'USER-ROLE-REMOVE-REQUEST', data: { user_id, role_id }};
    _websocket.send(JSON.stringify(message));
  });
}

// === ROLE DETAILS ===

//-----------------------------------------------------------------
export function requestRoles() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'ROLES-REQUEST' };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function roleAdd( name ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'ROLE-CREATE', data: { name } };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function removeRole( id ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'ROLE-REMOVE', data: { id }};
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function roleModuleCreate( role_id, module_id ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'ROLE-MODULE-CREATE-REQUEST', data: { role_id, module_id }};
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function roleModuleRemove( role_id, module_id ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'ROLE-MODULE-REMOVE-REQUEST', data: { role_id, module_id }};
    _websocket.send(JSON.stringify(message));
  });
}

// === MODULE DETAILS	===

//-----------------------------------------------------------------
export function requestModules() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'MODULE-REQUEST'};
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function moduleAdd( name ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'MODULE-CREATE', data: { name } };
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function moduleRemove(id) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'MODULE-REMOVE', data: { id }};
    _websocket.send(JSON.stringify(message));
  });
}

// === AUDIT-QC DETAILS	===
//-----------------------------------------------------------------
export function requestProkeyAuditQC() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'PROKEY-AUDIT-QC-REQUEST'};
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function prokeyAuditQCEdit( id, data ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'PROKEY-AUDIT-QC-UPDATE-REQUEST', data: { id, data }};
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function prokeyAuditQCRemove( id ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'PROKEY-AUDIT-QC-REMOVE-REQUEST', data: { data: id }};
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function prokeyAuditQCCreate( data ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'PROKEY-AUDIT-QC-CREATE-REQUEST', data: { data }};
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function requestTenants() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'TENANT-REQUEST'};
    _websocket.send(JSON.stringify(message));
  });
}

//-----------------------------------------------------------------
export function requestAuditModes() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'PROKEY-AUDIT-MODE-REQUEST'};
    _websocket.send(JSON.stringify(message));
  });
}

//#endregion

//#region [purple] === RECEIVE

// === SETTINGS ===

//-----------------------------------------------------------------
export function onCorrectionMode( cb ) {
  //-----------------------------------------------------------------
  _events.on( 'CORRECTION-MODE', cb );
}

//-----------------------------------------------------------------
export function onTaskMonitorStatus( cb ) {
  //-----------------------------------------------------------------
  _events.on( 'TASK-MONITOR-STATUS', cb );
}


// === TASK QUEUE ===

//-----------------------------------------------------------------
export function onTaskQueueInfo(cb) {
  //-----------------------------------------------------------------
  _events.on('TASK-QUEUE-INFO', cb );
}

//-----------------------------------------------------------------
export function onUpdateTaskQueueInfo(cb) {
  //-----------------------------------------------------------------
  _events.on('TASK-QUEUE-INFO-UPDATE', cb );
}

//-----------------------------------------------------------------
export function onRemoveTaskQueueItem(cb) {
  //-----------------------------------------------------------------
  _events.on('TASK-QUEUE-INFO-REMOVE', cb );
}

// === TASK ===

//-----------------------------------------------------------------
export function onTask(cb) {
  //-----------------------------------------------------------------
  focused_documents = [];
  _events.emit('DOCUMENT-FOCUS', []);
  _events.on('TASK', cb );
}

//-----------------------------------------------------------------
export function onTaskReset(cb) {
  //-----------------------------------------------------------------
  _events.on('TASK-RESET', cb );
}

//-----------------------------------------------------------------
export function onTaskUpdate(cb) {
  //-----------------------------------------------------------------
  _events.on('TASK-UPDATE', cb );
}

//-----------------------------------------------------------------
export function onTaskCancel(cb) {
  //-----------------------------------------------------------------
  _events.on('TASK-CANCEL', cb );
}

//-----------------------------------------------------------------
export function onClearTask(cb) {
  //-----------------------------------------------------------------
  _events.on('TASK-CLEAR', cb );
}

//-----------------------------------------------------------------
export function onDoctypes(cb) {
  //-----------------------------------------------------------------
  _events.on('DOCTYPES', cb );
}

// === DOCUMENTS ===

//-----------------------------------------------------------------
export function onSetDocuments(cb) {
  //-----------------------------------------------------------------
  _events.on('DOCUMENTS', cb );
}

//-----------------------------------------------------------------
export function onDocument(id, cb) {
  //-----------------------------------------------------------------
  _events.on('DOCUMENT-' + id, cb );
}

//-----------------------------------------------------------------
export function onDocumentFocus( cb ) {
  //-----------------------------------------------------------------
  _events.on( 'DOCUMENT-FOCUS', cb );
}

//-----------------------------------------------------------------
export function onDocumentFocusId( id, cb ) {
  //-----------------------------------------------------------------
  _events.on( 'DOCUMENT-FOCUS-' + id, cb );
}

//-----------------------------------------------------------------
export function onDocIdFromField( cb ) {
  //-----------------------------------------------------------------
  _events.once( 'DOCUMENT-ID', cb );
}

//-----------------------------------------------------------------
export function onDirectionIndicator( cb ) {
  //-----------------------------------------------------------------
  _events.on( 'DIRECTION-INDICATOR', cb );
}

// === BLOCKS ===

//-----------------------------------------------------------------
export function onBlock(id, cb) {
  //-----------------------------------------------------------------
  _events.on('BLOCK-' + id, cb );
}

//-----------------------------------------------------------------
export function onBlockFocus( cb ) {
  //-----------------------------------------------------------------
  _events.on( 'BLOCK-FOCUS', cb );
}

// === IMAGES ===

//-----------------------------------------------------------------
export function onImage(id, cb) {
  //-----------------------------------------------------------------
  _events.on('IMAGE-' + id, cb );
}

//-----------------------------------------------------------------
export function onImageMain(cb) {
  //-----------------------------------------------------------------
  _events.on('IMAGE', cb );
}

//-----------------------------------------------------------------
export function onImageFocus( id, cb ) {
  //-----------------------------------------------------------------
  _events.on( 'IMAGE-FOCUS-' + id, cb );
}


// === FIELDS ===

//-----------------------------------------------------------------
export function onField(id, cb) {
  //-----------------------------------------------------------------
  _events.on('FIELD-' + id, cb );
}

//-----------------------------------------------------------------
export function onFieldUpdate(id, cb) {
  //-----------------------------------------------------------------
  _events.on('FIELD-UPDATE-' + id, cb );
}

//-----------------------------------------------------------------
export function onFieldFocus( id, cb ) {
  //-----------------------------------------------------------------
  _events.on( 'FIELD-FOCUS-' + id, cb );
}

//-----------------------------------------------------------------
export function onFieldFocusImage( cb ) {
  //-----------------------------------------------------------------
  _events.on( 'FIELD-FOCUS', cb)
}

//-----------------------------------------------------------------
export function onAddFields(cb) {
  //-----------------------------------------------------------------
  _events.on('FIELD-CREATE', cb );
}

//-----------------------------------------------------------------
export function onRemoveFields(cb) {
  //-----------------------------------------------------------------
  _events.on('FIELD-REMOVE', cb );
}

//-----------------------------------------------------------------
export function onUpdateField(cb) {
  //-----------------------------------------------------------------
  _events.on('FIELD-UPDATE', cb );
}

// == MONITOR ===

//-----------------------------------------------------------------
export function onMonitorBatches(cb) {
  //-----------------------------------------------------------------
  _events.on('WORKFLOW-MONITOR-BATCHES', cb );
}

//-----------------------------------------------------------------
export function onMonitorModules(cb) {
  //-----------------------------------------------------------------
  _events.on('WORKFLOW-MONITOR-MODULES', cb );
}

//-----------------------------------------------------------------
export function onMonitorModulesUpdate(cb) {
  //-----------------------------------------------------------------
  _events.on('WORKFLOW-MONITOR-MODULES-UPDATE', cb );
}

//-----------------------------------------------------------------
export function onSetMonitor(cb) {
  //-----------------------------------------------------------------
  _events.on('WORKFLOW-MONITOR', cb );
}

//-----------------------------------------------------------------
export function onUpdateMonitor(cb) {
  //-----------------------------------------------------------------
  _events.on('WORKFLOW-MONITOR-UPDATE', cb );
}

// === ON-DEMAND ===

//-----------------------------------------------------------------
export function onOdBatchList(cb) {
  //-----------------------------------------------------------------
  _events.on('ON-DEMAND-BATCHES', cb );
}

//-----------------------------------------------------------------
export function onUpdateOdBatchList(cb) {
  //-----------------------------------------------------------------
  _events.on('ON-DEMAND-BATCHES-UPDATE', cb );
}

//-----------------------------------------------------------------
export function onRemoveOdBatchList(cb) {
  //-----------------------------------------------------------------
  _events.on('ON-DEMAND-BATCHES-REMOVE', cb );
}

// === LOAN COMPLETE ===

//-----------------------------------------------------------------
export function onFileInfo(cb) {
  //-----------------------------------------------------------------
  _events.on('FILE-INFO', cb );
}

//-----------------------------------------------------------------
export function onFileRules(cb) {
  //-----------------------------------------------------------------
  _events.on('FILE-RULES', cb );
}

//-----------------------------------------------------------------
export function onFileRuleInfo(cb) {
  //-----------------------------------------------------------------
  _events.on('FILE-RULE-INFO', cb );
}

//-----------------------------------------------------------------
export function onFileRuleDocuments(cb) {
  //-----------------------------------------------------------------
  _events.on('FILE-RULE-DOCUMENTS', cb );
}

//-----------------------------------------------------------------
export function onDocumentRules(cb) {
  //-----------------------------------------------------------------
  _events.on( 'DOCUMENT-RULES', cb );
}

//-----------------------------------------------------------------
export function onUsers(cb) {
  //-----------------------------------------------------------------
  _events.on('USERS', cb );
}

//-----------------------------------------------------------------
export function onRoles(cb) {
  //-----------------------------------------------------------------
  _events.on('ROLES', cb );
}

//-----------------------------------------------------------------
export function onAuditTask( cb ) {
  //-----------------------------------------------------------------
  _events.on( 'AUDIT-TASK', cb );
}

// === STATS ===

//-----------------------------------------------------------------
export function onStatsSchedule( cb ) {
  //-----------------------------------------------------------------
  _events.on( 'STATS-SCHEDULE', cb );
}


// === SYSTEM MANAGEMENT ===

export function onSystemMonitorStatus(cb) {
  //-----------------------------------------------------------------
  _events.on('SYSTEM-MONITOR-STATUS', cb );
}

export function onStopSystemMonitoring(cb) {
  //-----------------------------------------------------------------
  _events.on('MONITOR-STOP', cb );
}

export function onStartSystemMonitoring(cb) {
  //-----------------------------------------------------------------
  _events.on('MONITOR-START', cb );
}

export function onSystemRestart(cb) {
  //-----------------------------------------------------------------
  _events.on('SYSTEM-RESTART', cb );
}

export function onBatchReset(cb) {
  //-----------------------------------------------------------------
  _events.on('BATCH-RESET', cb );
}

export function onServerStartTime(cb) {
  //-----------------------------------------------------------------
  _events.on('SERVER-START-TIME', cb);
}

// === DATA PROCESSING ===

export function onTasksValidated(cb) {
  //-----------------------------------------------------------------
  _events.on('TASKS-VALIDATED-COUNT', cb );
}

export function onTasksAvailable(cb) {
  //-----------------------------------------------------------------
  _events.on('TASKS-AVAILABLE-COUNT', cb );
}

export function onUsersAvailable(cb) {
  //-----------------------------------------------------------------
  _events.on('USERS-AVAILABLE-COUNT', cb );
}

export function onUsersActive(cb) {
  //-----------------------------------------------------------------
  _events.on('USERS-ACTIVE-COUNT', cb );
}

// === RESOURCE UTILIZATION === 

export function onCPUMonitor(cb) {
  //-----------------------------------------------------------------
  _events.on('CPU-MONITOR', cb );
}

export function onMemoryMonitor(cb) {
  //-----------------------------------------------------------------
  _events.on('MEMORY-MONITOR', cb );
}

export function onSystemMemTotal(cb) {
  //-----------------------------------------------------------------
  _events.on('SYSTEM-MEMORY-TOTAL-MONITOR', cb );
}

// === ROLE DETAILS ===

export function onRoleAdd(cb) {
  //-----------------------------------------------------------------
  _events.on('ROLE-CREATE', cb );
}

export function onRemoveRole(cb) {
  //-----------------------------------------------------------------
  _events.on('ROLE-REMOVE', cb );
}

export function onRoleModuleAdd(cb) {
  //-----------------------------------------------------------------
  _events.on('ROLE-MODULE-ADD', cb );
}
// === USER DETAILS	===

export function onDisabledUsers(cb) {
  //-----------------------------------------------------------------
  _events.on('DISABLED-USERS-DETAILS', cb );
}

export function onAddBulkUsers(cb) {
  //-----------------------------------------------------------------
  _events.on('USERS-BULK-CREATE', cb );
}

export function onUserAdd(cb) {
  //-----------------------------------------------------------------
  _events.on('USER-CREATE', cb );
}

export function onUserEdit(cb) {
  //-----------------------------------------------------------------
  _events.on('USER-EDIT', cb );
}

//-----------------------------------------------------------------
export function onUserListDownload(cb) {
  //-----------------------------------------------------------------
  _events.on('USER-LIST-DOWNLOAD', cb );
}

// export function onPasswordReset(cb) {
//   //-----------------------------------------------------------------
//   _events.on('PASSWORD-RESET', cb );
// }

export function onUserDisable(cb) {
  //-----------------------------------------------------------------
  _events.on('USER-DISABLE', cb );
}

export function onUserEnable(cb) {
  //-----------------------------------------------------------------
  _events.on('USER-ENABLE', cb );
}

export function onUserRoleAdd(cb) {
  //-----------------------------------------------------------------
  _events.on('USER-ROLE-ADD', cb );
}

// === MODULE DETAILS	===

//-----------------------------------------------------------------
export function onModules(cb) {
  //-----------------------------------------------------------------
  _events.on('MODULES', cb );
}

//-----------------------------------------------------------------
export function onModuleAdd(cb) {
  //-----------------------------------------------------------------
  _events.on('MODULE-CREATE', cb );
}

//-----------------------------------------------------------------
export function onModuleRemove(cb) {
  //-----------------------------------------------------------------
  _events.on('MODULE-REMOVE', cb );
}

// === AUDIT-QC DETAILS	===
//-----------------------------------------------------------------
export function onProkeyAuditQC(cb) {
  //-----------------------------------------------------------------
  _events.on('PROKEY-AUDIT-QC', cb );
}

//-----------------------------------------------------------------
export function onTenants(cb) {
  //-----------------------------------------------------------------
  _events.on('TENANTS', cb );
}

//-----------------------------------------------------------------
export function onAuditModes(cb) {
  //-----------------------------------------------------------------
  _events.on('PROKEY-AUDIT-MODES', cb );
}


//#endregion

//-----------------------------------------------------------------
export function requestFieldFocus( field_id ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'FIELD-FOCUS-REQUEST', data: { id: field_id } };
    _websocket.send( JSON.stringify( message ) );
  });
}

//-----------------------------------------------------------------
export function requestCurrentFieldFocus() {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'FIELD-FOCUS-REQUEST', data: { } };
    _websocket.send( JSON.stringify( message ) );
  });
}

//-----------------------------------------------------------------
export function requestPreviousFieldFocus( field_id ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'FIELD-FOCUS-REQUEST', data: { id: field_id, type: 'prev' } };
    _websocket.send( JSON.stringify( message ) );
  });
}

//-----------------------------------------------------------------
export function moveDocument( document_active, document_over, is_above ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'DOCUMENT_MOVE', data: { document_active, document_over, is_above } };
    _websocket.send( JSON.stringify( message ) );
  });
}

//-----------------------------------------------------------------
export function moveImage( image_active, image_over, is_above ) {
  //-----------------------------------------------------------------
  send( () => {
    const message = { type: 'IMAGE_MOVE', data: { image_active, image_over, is_above } };
    _websocket.send( JSON.stringify( message ) );
  });
}

//-----------------------------------------------------------------
export function setMainImage( image ) {
  //-----------------------------------------------------------------
  if( !image ) return;
  if( focused_image ) _events.emit( 'IMAGE-FOCUS-' + focused_image.id, { clear: true } );
  _events.emit( 'IMAGE-FOCUS-' + image.id , {  } );
  focused_image = image;
  _events.emit( 'IMAGE', image );
}

//-----------------------------------------------------------------
export function setMainField( field ) {
  //-----------------------------------------------------------------
  requestFieldFocus( field.id ); 

  _events.emit( 'FIELD-FOCUS-' + field.id );
  _events.emit( 'FIELD-FOCUS', field );
  focused_field = field;
}

//-----------------------------------------------------------------
export function returnFieldFocus( ) {
  //-----------------------------------------------------------------
  focused_documents.forEach( doc => {
    _events.emit( 'DOCUMENT-FOCUS-' + doc.id, {open: true} );
  })
  if( focused_field ) _events.emit( 'FIELD-FOCUS-' + focused_field.id );
}

//-----------------------------------------------------------------
function isConnecting() {
  return _connecting || _websocket?.readyState === WebSocket.CONNECTING;
}

//-----------------------------------------------------------------
function isOpen() {
  //-----------------------------------------------------------------
  return _websocket?.readyState === WebSocket.OPEN;
}


//-----------------------------------------------------------------
export function isFocusedDocument( id ) {
  //-----------------------------------------------------------------
  let this_doc = focused_documents.find( (x) => x.id === id);
  if(!this_doc) return false;
  else if(this_doc && this_doc.open ) return true;
  else return false;
}
//-----------------------------------------------------------------
export function isFocusedField( id ) {
  //-----------------------------------------------------------------
  return focused_field?.id === id;
}
//-----------------------------------------------------------------
export function getFocusedField() {
  //-----------------------------------------------------------------
  return focused_field;
}
//-----------------------------------------------------------------
export function isFocusedImage( id ) {
  //-----------------------------------------------------------------
  return focused_image?.id === id;
}
//-----------------------------------------------------------------
export function getFocusedImage() {
  //-----------------------------------------------------------------
  return focused_image;
}